import { Component } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';

/**
 * The personal workspace preferences: the sidebar's main menu order, and which widgets stay
 * available while you work. The sidebar itself is no longer draggable - a menu you navigate with
 * shouldn't move under a stray drag - so this popup is the only place the order changes.
 *
 * Opened with the reorderable items as `{ key, description, iconUrl, icon }`, which the sidebar has already worked
 * out; a copy is taken so closing without saving leaves the menu as it was. Saving persists the order (per user, per
 * organisation) and closes with the keys in their new order, for the sidebar to apply without re-fetching.
 *
 * Both preferences go up under one Save changes, in one request. The widget switch has to be
 * server-side rather than in the browser: a switch saved with that button but only locally would
 * be a lie the first time somebody signed in on another machine.
 */
@Component({
	selector: 'modal-customise-sidebar',
	templateUrl: './modal-customise-sidebar.component.html'
})
export class ModalCustomiseSidebarComponent extends UIModalBaseComponent {

	items: any[] = [];
	widgets = { attention: true };
	disabled = false;

	constructor(
		public app: AppService,
		private api: ApiService,
	) {
		super();
	}

	open(data: any) {
		this.items = (data?.items || []).map(item => ({ ...item }));
		// Read from org info rather than kept here, so the switch shows what the server holds even
		// if this popup has never been opened in the session.
		this.widgets = { attention: this.app.attentionWidgetEnabled };
		this.disabled = false;
		this.modal.open();
	}

	itemDrop(event) {
		if (event.previousIndex === event.currentIndex) return;

		const item = this.items.splice(event.previousIndex, 1)[0];
		this.items.splice(event.currentIndex, 0, item);
	}

	save() {
		const order = this.items.map(item => item.key);
		const widgets = { ...this.widgets };

		this.disabled = true;
		this.api.organisation.navigationOrder({ order, widgets }, () => {
			this.disabled = false;
			// Applied to org info here so the widget appears or goes on save, rather than at the
			// next refresh; the server already holds the same value. The emit is what makes a
			// widget switched back on fetch its rows - it has been hidden and has nothing to show.
			if (this.app.orgInfo) {
				this.app.orgInfo.widget_prefs = widgets;
				this.app.onOrgInfoChange.emit(this.app.orgInfo);
			}
			this.modal.close(order);
			this.app.notifications.showSuccess('Preferences saved.');
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

}
